// authService.ts

export const getToken = () => localStorage.getItem('token');

const DEVICE_ID_KEY = 'nict-device-id';
export const BACKEND_STATUS_EVENT = 'nict:backend-status';

export interface BackendStatusDetail {
    isDown: boolean;
    message: string;
    status?: number;
}

export const devBaseUrl = 'http://localhost:8080';
export const prodBaseUrl = 'https://api.nictcomputereducation.com:8443';

export const baseUrl = true ? prodBaseUrl:devBaseUrl;

const backendDownStatuses = new Set([500, 502, 503, 504]);
let lastBackendState: boolean | null = null;

const notifyBackendStatus = (detail: BackendStatusDetail) => {
    if (lastBackendState === detail.isDown) {
        return;
    }
    lastBackendState = detail.isDown;
    window.dispatchEvent(new CustomEvent<BackendStatusDetail>(BACKEND_STATUS_EVENT, { detail }));
};

const handleBackendStatusFromResponse = (response: Response) => {
    if (backendDownStatuses.has(response.status)) {
        notifyBackendStatus({
            isDown: true,
            status: response.status,
            message: 'Backend is temporarily unavailable. Some features may not work right now.',
        });
        return;
    }

    notifyBackendStatus({
        isDown: false,
        status: response.status,
        message: '',
    });
};

export const apiFetch = async (url: string, options?: RequestInit): Promise<Response> => {
    try {
        const response = await fetch(baseUrl + url, options);
        handleBackendStatusFromResponse(response);
        return response;
    } catch (error) {
        notifyBackendStatus({
            isDown: true,
            message: 'Unable to reach backend. Please check server status and try again.',
        });
        throw error;
    }
};

export const getClientPlatform = () => {
    if (typeof navigator === 'undefined') return 'desktop';
    return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
};

export const getOrCreateDeviceId = () => {
    if (typeof localStorage === 'undefined') return 'nict-web-device';
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) {
        return existing;
    }

    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `nict-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, generated);
    return generated;
};

export const authenticatedFetch = async (url: string, options?: RequestInit): Promise<Response> => {
    const token = getToken();
    const method = options?.method || 'GET';

    if (url.includes('/api/admin/attendance/trainees/')) {
        console.log('[DEBUG authenticatedFetch]', method, url);
    }
    
    const headers = {
        ...(options?.headers || {'Content-Type': 'application/json'}),
        'Authorization': token ? `Bearer ${token}` : '',
    };

    const requestOptions: RequestInit = {
        ...options,
        headers,
    };

    const response = await apiFetch(url, requestOptions);

    if (response.status === 401) {
        const clone = response.clone();
        try {
            const text = await clone.text();
            if (text && text.includes('SESSION_REPLACED')) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                localStorage.removeItem('name');
                localStorage.removeItem('role');
                localStorage.removeItem('branch');
                window.dispatchEvent(new CustomEvent('nict:session-replaced'));
            }
        } catch {
            // ignore parse errors; caller will still handle the failed response
        }
    }

    return response;
};
